import {
  Attendee,
  EventDetail,
  EventDetailApiContent,
  EventDetailApiImage,
  EventDetailApiMember,
  Speaker,
} from '../models/event.models';

/** Avatar do membro (string ou `{ url }`) — reutilizado em inscrições e depoimentos. */
export function memberAvatarUrl(m: EventDetailApiMember | null | undefined): string {
  if (!m) return '/assets/avatars/_placeholder.png';
  const pick = (raw: string | { url: string } | null | undefined): string | null => {
    if (raw == null) return null;
    const url = typeof raw === 'string' ? raw : raw.url;
    const t = url?.trim();
    return t || null;
  };
  return pick(m.avatar_thumb) || pick(m.avatar) || '/assets/avatars/_placeholder.png';
}

function mapRegistrationsToAttendees(c: EventDetailApiContent): Attendee[] {
  const rows: Attendee[] = [];
  for (const r of c.event_registrations ?? []) {
    const m = r.member;
    const pid = m?.public_id?.trim();
    const name = m?.name?.trim();
    if (!pid || !name) continue;
    rows.push({
      id: pid,
      name,
      avatarUrl: memberAvatarUrl(m ?? undefined),
    });
  }
  return rows;
}

function parsePrice(value: number | string | null | undefined): number {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  const n = parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/** Formata horário da programação (ISO ou `HH:MM`). */
export function formatScheduleTime(timeStr: string): string {
  const d = new Date(timeStr);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  const m = timeStr.match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : timeStr;
}

/** Texto ao lado do lote: data e hora de fim da venda (`end_date`). */
function formatTicketUntil(endDate: string | null): string | undefined {
  if (!endDate) return undefined;
  const d = new Date(endDate);
  if (Number.isNaN(d.getTime())) return undefined;
  const data = d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const hora = d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `Até ${data} às ${hora}`;
}

function buildVenueName(c: EventDetailApiContent): string {
  if (c.location?.trim()) return c.location.trim();
  const parts = [c.address, c.city, c.state].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

function buildSpeakers(speakerName: string | null, placeholderAvatar: string): Speaker[] {
  if (!speakerName?.trim()) return [];
  return [
    {
      id: 'speaker',
      name: speakerName.trim(),
      avatarUrl: placeholderAvatar,
      isVerified: false,
    },
  ];
}

/**
 * Converte o `content` de GET /private/app/events/get_detail para o modelo de UI (`EventDetail`).
 */
export function mapEventDetailApiToViewModel(
  c: EventDetailApiContent,
  defaultBanner: string,
): EventDetail {
  const dateISO =
    c.start_datetime ?? c.end_datetime ?? new Date().toISOString();

  const cover = c.cover_image?.trim() || '';
  const sortedExtras = [...(c.event_images ?? [])].sort(
    (a, b) => a.position - b.position,
  );
  const heroCarouselUrls: string[] = [];
  if (cover) heroCarouselUrls.push(cover);
  for (const img of sortedExtras) {
    if (img.image_url?.trim()) heroCarouselUrls.push(img.image_url.trim());
  }
  if (heroCarouselUrls.length === 0) heroCarouselUrls.push(defaultBanner);
  const bannerUrl = heroCarouselUrls[0];

  const lots = (c.event_tickets ?? []).map((t) => {
    const price = parsePrice(t.price);
    const isAlreadyRegistered = t.payment_link === true;
    const available = t.is_available !== false;

    let soldOut: boolean;
    let actionLabel: string;
    if (!available) {
      soldOut = true;
      actionLabel = 'Esgotado';
    } else if (!t.is_active) {
      soldOut = true;
      actionLabel = 'Indisponível';
    } else if (isAlreadyRegistered) {
      soldOut = true;
      actionLabel = 'Inscrito';
    } else {
      soldOut = false;
      actionLabel = 'Comprar';
    }

    return {
      id: String(t.id),
      name: t.batch,
      until: formatTicketUntil(t.end_date),
      price,
      soldOut,
      paymentLink: t.payment_link,
      actionLabel,
    };
  });

  const agenda = [...(c.event_schedules ?? [])]
    .sort((a, b) => {
      const ta = formatScheduleTime(a.time);
      const tb = formatScheduleTime(b.time);
      const cmp = ta.localeCompare(tb);
      return cmp !== 0 ? cmp : a.id - b.id;
    })
    .map((s) => ({
      time: formatScheduleTime(s.time),
      title: s.description,
    }));

  const descriptionHtml =
    (c.description && c.description.trim()) ||
    '<p class="text-gray-500">Sem descrição para este evento.</p>';

  return {
    id: c.id,
    title: (c.theme && c.theme.trim()) || 'Evento',
    descriptionTitle: c.description_title ?? null,
    dateISO,
    bannerUrl,
    heroCarouselUrls,
    speakers: buildSpeakers(c.speaker_name, defaultBanner),
    venue: {
      name: buildVenueName(c),
      address: c.address ?? undefined,
      city: c.city ?? undefined,
      mapUrl: c.google_maps_link ?? undefined,
    },
    lots,
    description: descriptionHtml,
    agenda,
    attendees: mapRegistrationsToAttendees(c),
    gallery: (c.event_images ?? [])
      .sort((a, b) => a.position - b.position)
      .map((img: EventDetailApiImage) => img.image_url),
  };
}
